/**
 * Plan Spine — view-model shaping for The Plan page.
 *
 * Pure, framework-free transform: a raw `cycle_reports` row (as read from
 * Supabase) becomes the plain shape ThePlanPage renders. Kept separate from
 * the data-fetch so the shaping logic can be unit-tested with fixtures
 * instead of a live database (see planViewModel.test.ts).
 *
 * `plan_of_record` is stored as untyped jsonb at the DB layer, so every field
 * read out of it is narrowed defensively — a malformed or partial row must
 * degrade to "no items" rather than throw.
 */

// ── Raw shapes (what we read) ───────────────────────────────────────────────

/** One entry of `plan_of_record.ranked_items`, narrowed to what this page needs. */
export interface RawPlanOfRecordItem {
  rank: number;
  item_id: string;
  title: string;
  above_cut_line: boolean;
}

/** Narrowed shape of the `plan_of_record` jsonb column. */
export interface RawPlanOfRecord {
  ranked_items: RawPlanOfRecordItem[];
  cut_line_position: number | null;
}

/** The `cycle_reports` columns ThePlanPage reads. */
export interface RawCycleReportRow {
  id: string;
  cycle_id: string;
  cycle_name: string | null;
  version: number;
  supersedes_id: string | null;
  rationale: string | null;
  proposal_id: string | null;
  amendment_log: unknown;
  ratified_at: string | null;
  ratified_by: string | null;
  legitimacy_state: string;
  plan_of_record: unknown;
}

// ── View model (what we render) ─────────────────────────────────────────────

export interface PlanItemViewModel {
  id: string;
  rank: number;
  title: string;
  aboveCutLine: boolean;
}

export interface PlanViewModel {
  reportId: string;
  /** Always non-empty — falls back to "The plan" when the cycle has no name. */
  cycleName: string;
  /** Pre-formatted for display, e.g. "August 10, 2026". Null when unratified. */
  ratifiedAtLabel: string | null;
  /** Raw user id — the caller resolves this to a display name (see useActorDisplayMap). */
  ratifiedByUserId: string | null;
  aboveCut: PlanItemViewModel[];
  belowCut: PlanItemViewModel[];
  cutLinePosition: number | null;
  /** False when the report exists but carries no ranked items. */
  hasItems: boolean;
}

// ── Parsing ──────────────────────────────────────────────────────────────────

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRawItem(value: unknown): value is RawPlanOfRecordItem {
  if (!isPlainObject(value)) return false;
  return (
    typeof value.item_id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.rank === 'number' &&
    typeof value.above_cut_line === 'boolean'
  );
}

/** Defensive jsonb parse — never throws; missing/malformed fields become empty/null. */
export function parsePlanOfRecord(value: unknown): RawPlanOfRecord {
  if (!isPlainObject(value)) {
    return { ranked_items: [], cut_line_position: null };
  }
  const items = Array.isArray(value.ranked_items) ? value.ranked_items.filter(isRawItem) : [];
  const cutLinePosition = typeof value.cut_line_position === 'number' ? value.cut_line_position : null;
  return { ranked_items: items, cut_line_position: cutLinePosition };
}

function formatRatifiedDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Transform ────────────────────────────────────────────────────────────────

/** Report row -> the plain-language shape ThePlanPage renders. Pure — no I/O. */
export function buildPlanViewModel(row: RawCycleReportRow): PlanViewModel {
  const por = parsePlanOfRecord(row.plan_of_record);
  const ranked = [...por.ranked_items].sort((a, b) => a.rank - b.rank);

  const toViewItem = (item: RawPlanOfRecordItem): PlanItemViewModel => ({
    id: item.item_id,
    rank: item.rank,
    title: item.title,
    aboveCutLine: item.above_cut_line,
  });

  return {
    reportId: row.id,
    cycleName: row.cycle_name?.trim() || 'The plan',
    ratifiedAtLabel: formatRatifiedDate(row.ratified_at),
    ratifiedByUserId: row.ratified_by,
    aboveCut: ranked.filter((i) => i.above_cut_line).map(toViewItem),
    belowCut: ranked.filter((i) => !i.above_cut_line).map(toViewItem),
    cutLinePosition: por.cut_line_position,
    hasItems: ranked.length > 0,
  };
}
